import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { isHTML } from "../../../utils/uiUtility";

const sanitizeHTML = (htmlString) => {
  // Allow GoA web components used in resource content without permitting event handlers.
  let htmlValue = DOMPurify.sanitize(htmlString, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["target", "type", "mt", "mb", "size"],
    ALLOWED_ATTR: ["href", "target", "rel", "type", "mt", "mb", "size"],
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: /^goa-/,
      attributeNameCheck: /^(type|mt|mb|size)$/,
    },
  });

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlValue;

  // Remove any target attributes that aren't "_blank" for security
  const targetElements = tempDiv.querySelectorAll("[target]");
  targetElements.forEach((element) => {
    const target = element.getAttribute("target");
    if (target !== "_blank") {
      element.removeAttribute("target");
    } else {
      // Ensure rel="noopener" for security when target="_blank"
      element.setAttribute("rel", "noopener noreferrer");
    }
  });

  return {
    __html: tempDiv.innerHTML,
  };
};

const DisplayContent = ({ content, clickHandlers }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      const elements = containerRef.current.querySelectorAll("a[href^='#']");
      const eventListeners = [];

      elements.forEach((element) => {
        const href = element.getAttribute("href");
        const functionName = href.substring(1);
        const targetElement = document.getElementById(functionName);

        let clickHandler = (e) => {
          e.preventDefault();
          if (clickHandlers && clickHandlers[functionName]) {
            clickHandlers[functionName]();
          } else if (targetElement) {
            targetElement.focus();
            targetElement.scrollIntoView({ behavior: "smooth" });
          }
        };

        element.addEventListener("click", clickHandler);
        eventListeners.push({ element, clickHandler });
      });

      // Cleanup function
      return () => {
        eventListeners.forEach(({ element, clickHandler }) => {
          element.removeEventListener("click", clickHandler);
        });
      };
    }
  }, [content, clickHandlers]);

  return (
    <>
      {isHTML(content) ? (
        <span
          ref={containerRef}
          dangerouslySetInnerHTML={sanitizeHTML(content)}
        />
      ) : (
        <>{content}</>
      )}
    </>
  );
};

export default DisplayContent;
